import { capitalize } from '../utils';
import YesNo from '../components/builder/components/YesNo';
import Range from '../components/builder/components/Range';
import FloatRange from '../components/builder/components/FloatRange';
import MultiSelectRange from '../components/builder/components/MultiSelectRange';
import MultiSelectGeography from '../components/builder/components/MultiSelectGeography';
import MultiSelectOptions from '../components/builder/components/MultiSelectOptions';
import dataDictionary from '../assets/data-dictionary/dataDictionary.json';
import {
  ApiExpressionNode,
  ApiExpressionLeaf,
  ExpressionLeaf,
  ExpressionNode,
  MyDataFilter,
  MyDataState,
  VariableFilter,
  VariableState,
} from '../types/dataDictionary';
import {
  MODEL_SCORE_MAX,
  MODEL_SCORE_MIN,
  createAudienceLeaf,
  createFileLeaf,
  createGroup,
  createLeaf,
  createModelLeaf,
  createZipLeaf,
  flattenLeaves,
  isAudienceLeaf,
  isFileLeaf,
  isLeaf,
  isModelLeaf,
  isValidModelScoreRange,
  isVariableLeaf,
  isZipLeaf,
} from './expressionTree';

const componentMap = {
  boolean: YesNo,
  range: Range,
  'float-range': FloatRange,
  'multi-select-range': MultiSelectRange,
  'multi-select': MultiSelectGeography,
  'multi-select-options': MultiSelectOptions,
};

const validZoneTypes = ['include', 'exclude'];

/**
 * Top-level categories that carry a "New" badge in the data dictionary UI.
 * Names must match the top-level keys of dataDictionary.json exactly.
 *
 * Set by hand for each taxonomy refresh — currently 3 of the categories
 * added or rebuilt in the SEPT-2026 refresh. Clearing a badge means removing
 * the name from this list at the next refresh.
 *
 * Deliberately here rather than in dataDictionary.json: a category node's
 * keys are `attributes` plus its subcategory names, with no metadata slot,
 * so `traverse` below and the dictionary UIs would read a flag in the JSON
 * as a subcategory unless every consumer skipped it by name (the way
 * `categoryDescription` has to be skipped in DataDictionary.tsx). On top of
 * that, data-dictionary/csvToJsonDataDictionary.ts rebuilds the dictionary
 * from scratch on each refresh, which would wipe any hand-added field.
 */
export const NEW_CATEGORIES: ReadonlySet<string> = new Set([
  'Media',
  'Political',
  'Purchase Behavior',
]);

export function isNewCategory(category: string): boolean {
  return NEW_CATEGORIES.has(category);
}

/**
 * Attribute keys withheld from the attribute pickers (Audience Builder and
 * Enhancements) while their data or structure is reworked. Ask Addy's
 * taxonomy (server/services/askAddy/taxonomy.js) withholds the same keys.
 *
 * The entries stay in dataDictionary.json on purpose: audiences already saved
 * with them keep resolving and their filters keep working.
 *
 * Empty today. B2B_NAICS_Code was withheld on 2026-09-24 while its option list
 * was rebuilt from the data (NAICS 2017 → the codes the universe actually
 * carries) and its enhancement export was fixed.
 */
export const HIDDEN_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set<string>();

export function isHiddenAttribute(key: string | undefined): boolean {
  return key !== undefined && HIDDEN_ATTRIBUTE_KEYS.has(key);
}

export const filePrefixesMap = {
  upload: 'uploaded',
  purchasedAudience: 'purchased',
  purchased: 'purchased',
};

export const parseId = (id) => {
  return id.split('___');
};

export const getParentFromId = (id) => {
  const idPath = parseId(id);
  return idPath[idPath.length - 2];
};

export const getAttributeById = (dictionary, id) => {
  const idPath = id.split('___');

  let attribute;
  let curParent;
  idPath.forEach((selector, idx) => {
    if (idx === idPath.length - 1) {
      attribute = {
        ...curParent?.attributes?.find(
          ({ attributeName }) => attributeName === selector
        ),
      };
    }
    curParent = curParent ? curParent[selector] : dictionary[selector];
  });

  return attribute;
};

export const getComponent = (dictionary, id) => {
  const attribute = getAttributeById(dictionary, id);
  const Component = componentMap[attribute.attrs.componentType] || YesNo;
  return { Component, attrs: attribute.attrs };
};

export function* traverse(object, path = []) {
  for (const key of Object.keys(object)) {
    const keyPath = path.concat(key);
    yield [key, object[key], keyPath, object];

    if (
      object[key] !== null &&
      typeof object[key] === 'object' &&
      key !== 'attributes'
    ) {
      yield* traverse(object[key], keyPath);
    }
  }
}

export const myDataZoneType = (zoneType) => {
  const capitalizedZoneType = capitalize(zoneType);
  return `myData${capitalizedZoneType}`;
};

// ===========================================================================
// builder state -> filters (PR 2b-1: AST shape)
// ===========================================================================

/**
 * Convert one leaf's VariableState into an ApiExpressionLeaf — the wire shape
 * the server's translateNode consumes. Pulls min/max or values out of the
 * leaf's `state` field according to the component type. Mirrors the legacy
 * `convertVariablesToFilters` logic per-leaf.
 */
function convertVariableLeafToApiLeaf(variable: VariableState): ApiExpressionLeaf {
  const { id, attrs, state, attributeName } = variable;
  const { key, value, componentType } = attrs;

  switch (componentType) {
    case 'range':
    case 'float-range': {
      if (!state || state.min == null || state.max == null) {
        throw new Error(`${attributeName} is missing min/max.`);
      }
      const { min, max } = state;
      return { type: 'leaf', key, min: Number(min), max: Number(max) } as ApiExpressionLeaf;
    }
    case 'multi-select-range':
    case 'multi-select-options':
    case 'multi-select': {
      if (!state || state.values == null || state.values.length === 0) {
        throw new Error(`${attributeName} cannot be empty.`);
      }
      return {
        type: 'leaf',
        key,
        values: state.values.map(({ value: v }: { value: string }) => v),
      };
    }
    default: {
      // boolean: the attribute's static `value` field is the IN-list value.
      return { type: 'leaf', key, values: [value] };
    }
  }
  // The id is intentionally NOT propagated to the wire — the server only
  // needs `key`. Front-end nodeId stays client-only.
  // (id is unused outside this block; suppress the unused-var warning by
  // referencing it once in the comment above and not assigning it.)
}

/**
 * Convert a client-side leaf to the wire shape, dispatching by kind.
 * Membership leaves carry only their id; the server's translator emits
 * the corresponding subquery.
 *
 * ATAD-146: throws on a leaf kind it doesn't know. It used to return null,
 * and the caller dropped the leaf — so the count and the saved audience
 * silently lost a filter (an include got wider, an exclude disappeared).
 */
function clientLeafToApiLeaf(leaf: ExpressionLeaf): ApiExpressionLeaf {
  if (isAudienceLeaf(leaf)) {
    return { type: 'leaf', kind: 'audience', audienceId: leaf.audienceId };
  }
  if (isFileLeaf(leaf)) {
    return { type: 'leaf', kind: 'file', fileId: leaf.fileId };
  }
  if (isZipLeaf(leaf)) {
    // ATAD-136: never the ZIP list — the server fills it in from the file.
    // The name travels so a saved audience can label its card on reload.
    return { type: 'leaf', kind: 'zip', fileId: leaf.fileId, name: leaf.name };
  }
  if (isModelLeaf(leaf)) {
    // ATAD-146: the range is required and always sent whole. The server
    // checks it too, but a broken range must not get that far.
    if (!isValidModelScoreRange(leaf.scoreMin, leaf.scoreMax)) {
      throw new Error(
        `${leaf.name || 'A model'} needs a score range between ${MODEL_SCORE_MIN} and ${MODEL_SCORE_MAX}.`
      );
    }
    return {
      type: 'leaf',
      kind: 'model',
      fileId: leaf.fileId,
      name: leaf.name,
      scoreMin: leaf.scoreMin,
      scoreMax: leaf.scoreMax,
    };
  }
  if (isVariableLeaf(leaf)) {
    return convertVariableLeafToApiLeaf(leaf.variable);
  }
  throw new Error(
    `This audience has a filter of an unknown kind ("${(leaf as { kind?: string }).kind}"). Remove it and try again.`
  );
}

/**
 * Walk a client-side expression tree and emit the API-shape AST. Group nodes
 * become `{type, children}` (no nodeId); leaves become ApiExpressionLeaf.
 * Empty groups are pruned; single-child groups collapse to that child.
 */
function expressionTreeToApi(
  root: ExpressionNode | null,
): ApiExpressionNode | null {
  if (!root) return null;
  if (isLeaf(root)) return clientLeafToApiLeaf(root);
  const children = root.children
    .map(expressionTreeToApi)
    .filter((c): c is ApiExpressionNode => c !== null);
  if (children.length === 0) return null;
  if (children.length === 1) return children[0];
  return { type: root.type, children };
}

export const convertBuilderStateToFilters = (state: any) => {
  const {
    expression,
    excludeExpression,
    includeOnlyOneRecordPerHousehold,
  } = state;

  // PR 2c: audiences and files are now leaves inside the expression tree.
  // The top-level audiences[]/files[] arrays remain in the wire shape (empty)
  // so server-side back-compat code that iterates them stays a no-op rather
  // than crashing on missing fields.
  return {
    variables: {
      expression: expressionTreeToApi(expression),
      excludeExpression: expressionTreeToApi(excludeExpression),
      options: { includeOnlyOneRecordPerHousehold },
    },
    files: { include: [], exclude: [] },
    audiences: { include: [], exclude: [] },
  };
};

export const convertFilesToFilters = (files: MyDataState[]): MyDataFilter[] =>
  files.map(({ id, name }) => ({
    id,
    name,
    type: 'file',
  }));

export const convertAudiencesToFilters = (
  audiences: MyDataState[]
): MyDataFilter[] =>
  audiences.map(({ id, name }) => ({ id, name, type: 'audience' }));

// Note: the legacy `convertVariablesToFilters(VariableState[]) -> VariableFilter[]`
// function was removed in PR 2b-1. The conversion is now expression-tree-aware
// and lives in `convertVariableLeafToApiLeaf` + `expressionTreeToApi` above.
// Reverse conversion still emits flat `VariableState[]` via
// `convertVariablesFiltersToBuilderFormat` for any caller that needs it.

// end: builder state -> filters

// ===========================================================================
// filters -> builder state (PR 2b-1: handles both legacy and AST shapes)
// ===========================================================================
//
// Saved audiences in MongoDB live in the legacy shape (variables.include[]
// etc.). New-shape audiences saved by the new front-end will arrive with
// `expression` / `excludeExpression`. The loader here detects shape and
// produces an expression tree the reducer can hydrate directly.

/**
 * Build a per-key index of dataDictionary attributes so we can look up the
 * full VariableState from a leaf's `key` (or legacy `id`) without re-walking
 * the dictionary on every lookup.
 */
function buildKeyToAttributeIndex(): {
  byKey: Map<string, any>;
  byId: Map<string, any>;
} {
  const byKey = new Map<string, any>();
  const byId = new Map<string, any>();
  for (const [, value] of traverse(dataDictionary as any)) {
    const v = value as any;
    if (v && Array.isArray(v.attributes)) {
      for (const attr of v.attributes) {
        if (attr?.attrs?.key) byKey.set(attr.attrs.key, attr);
        if (attr?.id) byId.set(attr.id, attr);
      }
    }
  }
  return { byKey, byId };
}

/**
 * Hydrate a leaf-shaped filter (legacy VariableFilter or ApiExpressionLeaf)
 * into a fully-populated VariableState by looking up the matching attribute
 * in the data dictionary and copying min/max/values into the leaf's state.
 *
 * Returns null if no attribute matches (silently skipped, mirroring the
 * legacy loader's behavior).
 */
function hydrateVariableLeaf(
  filter: { id?: string; key?: string; values?: any[]; min?: number; max?: number },
  index: { byKey: Map<string, any>; byId: Map<string, any> },
): VariableState | null {
  const attr = (filter.id && index.byId.get(filter.id)) || (filter.key && index.byKey.get(filter.key));
  if (!attr) return null;

  const incomingValues = filter.values ?? [];
  const hydrated: VariableState = {
    ...attr,
    state: null,
  };

  switch (attr.attrs.componentType) {
    case 'boolean':
      // Boolean: only present in the audience if the saved value matches the
      // attribute's static value. Otherwise the leaf isn't reconstructed.
      if (!incomingValues.includes(attr.attrs.value)) return null;
      return hydrated;
    case 'range':
    case 'float-range':
      hydrated.state = { min: filter.min, max: filter.max };
      return hydrated;
    case 'multi-select':
    case 'multi-select-options':
    case 'multi-select-range':
      hydrated.state = { values: incomingValues } as any;
      return hydrated;
    default:
      return hydrated;
  }
}

/**
 * Build an expression tree from a legacy include[]/exclude[] array.
 * Mirrors the server's `upgradeLegacyFilters` adapter — multiple leaves on
 * the same key OR-join (per-key sub-tree); distinct keys AND-join under a
 * root group.
 */
function expressionFromLegacyArray(
  filters: VariableFilter[] | undefined,
  index: { byKey: Map<string, any>; byId: Map<string, any> },
): ExpressionNode | null {
  if (!filters || filters.length === 0) return null;

  // Group by key so per-key duplicates collapse into an OR sub-tree.
  const byKey = new Map<string, ExpressionNode[]>();
  for (const f of filters) {
    const variable = hydrateVariableLeaf(f, index);
    if (!variable) continue;
    const leaf = createLeaf(variable);
    const k = f.key ?? variable.attrs.key;
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k)!.push(leaf);
  }

  const perKey: ExpressionNode[] = [];
  for (const leaves of byKey.values()) {
    perKey.push(leaves.length === 1 ? leaves[0] : createGroup('or', leaves));
  }
  if (perKey.length === 0) return null;
  if (perKey.length === 1) return perKey[0];
  return createGroup('and', perKey);
}

/**
 * Convert an ApiExpressionNode (server-side AST) to a client-side
 * ExpressionNode. Dispatches by `kind` for leaves: variable leaves
 * hydrate from the data dictionary; audience/file leaves wrap their id
 * (and pull `name` from the legacy myData arrays during loading — see
 * the loader below for the back-compat path).
 *
 * ATAD-146: throws on a leaf kind it doesn't know. Unknown kinds used to
 * fall through to variable hydration, find no attribute and vanish — so
 * re-saving the audience silently dropped the filter.
 */
function expressionFromApiNode(
  node: ApiExpressionNode | null,
  index: { byKey: Map<string, any>; byId: Map<string, any> },
  membershipNames: { audiences: Map<string, string>; files: Map<string, string> },
): ExpressionNode | null {
  if (!node) return null;
  if (node.type === 'leaf') {
    if (node.kind === 'audience') {
      const name = membershipNames.audiences.get(node.audienceId) ?? '';
      return createAudienceLeaf({ id: node.audienceId, name });
    }
    if (node.kind === 'file') {
      const name = membershipNames.files.get(node.fileId) ?? '';
      return createFileLeaf({ id: node.fileId, name });
    }
    if (node.kind === 'zip') {
      // ATAD-136: stored ZIP leaves carry their own name and ZIP list.
      return createZipLeaf({
        id: node.fileId,
        name: node.name ?? '',
        zipCount: Array.isArray(node.values) ? node.values.length : undefined,
      });
    }
    if (node.kind === 'model') {
      // ATAD-146: stored model leaves carry their name and range. The
      // server's importId stays behind — it is re-resolved from the File on
      // every count and save. A stored leaf without a valid range is refused
      // rather than defaulted to 1–20, which would widen the audience.
      if (!isValidModelScoreRange(node.scoreMin, node.scoreMax)) {
        throw new Error(
          `The model ${node.name ? `"${node.name}" ` : ''}in this audience has no valid score range.`
        );
      }
      return createModelLeaf({
        id: node.fileId,
        name: node.name ?? '',
        scoreMin: node.scoreMin,
        scoreMax: node.scoreMax,
      });
    }
    // Widened on purpose: stored filters can hold kinds this build predates.
    const kind = (node as { kind?: string }).kind;
    if (kind != null && kind !== 'variable') {
      throw new Error(
        `This audience uses a filter of an unknown kind ("${kind}"). Refresh the page to load the latest version of the app.`
      );
    }
    // Variable leaf (kind === 'variable' or absent for back-compat).
    const variable = hydrateVariableLeaf(node as any, index);
    return variable ? createLeaf(variable) : null;
  }
  const children = node.children
    .map((c) => expressionFromApiNode(c, index, membershipNames))
    .filter((c): c is ExpressionNode => c !== null);
  if (children.length === 0) return null;
  if (children.length === 1) return children[0];
  return createGroup(node.type, children);
}

/**
 * Build membership leaves from legacy top-level `audiences.include[]` /
 * `files.include[]` arrays. Returned as a flat list to be merged into the
 * tree by the loader. Mirrors the server adapter's policy: legacy-shape
 * audiences/files become AND-joined leaves in the include / exclude tree.
 */
function membershipLeavesFromLegacyArrays(
  audiences: { id: string; name: string }[] | undefined,
  files: { id: string; name: string }[] | undefined,
): ExpressionNode[] {
  const leaves: ExpressionNode[] = [];
  for (const a of audiences ?? []) {
    leaves.push(createAudienceLeaf({ id: a.id, name: a.name ?? '' }));
  }
  for (const f of files ?? []) {
    leaves.push(createFileLeaf({ id: f.id, name: f.name ?? '' }));
  }
  return leaves;
}

/** Merge a flat list of additional nodes (membership leaves) into a tree. */
function mergeNodesIntoTree(
  tree: ExpressionNode | null,
  extras: ExpressionNode[],
): ExpressionNode | null {
  if (extras.length === 0) return tree;
  if (!tree) {
    return extras.length === 1 ? extras[0] : createGroup('and', extras);
  }
  // If the existing root is an AND, append. Otherwise wrap in a fresh AND.
  if (tree.type === 'and') {
    return { ...tree, children: [...tree.children, ...extras] };
  }
  return createGroup('and', [tree, ...extras]);
}

export const convertFiltersToBuilderFormat = (filtersJsonString: any) => {
  let filters: any = filtersJsonString;

  try {
    filters = JSON.parse(filtersJsonString);
  } catch (err) {
    console.error('Could not parse filters for saved audience.');
    return;
  }

  const { variables, files, audiences } = filters;
  const index = buildKeyToAttributeIndex();

  // Build a name lookup from the legacy myData arrays so AST audience/file
  // leaves can recover their display name. (Wire-shape AST leaves carry only
  // the id; saved audiences keep the human-readable name in the legacy arrays.)
  const membershipNames = {
    audiences: new Map<string, string>(),
    files: new Map<string, string>(),
  };
  for (const a of audiences?.include ?? []) membershipNames.audiences.set(a.id, a.name ?? '');
  for (const a of audiences?.exclude ?? []) membershipNames.audiences.set(a.id, a.name ?? '');
  for (const f of files?.include ?? []) membershipNames.files.set(f.id, f.name ?? '');
  for (const f of files?.exclude ?? []) membershipNames.files.set(f.id, f.name ?? '');

  // Detect shape: AST if either expression key is present, otherwise legacy.
  const hasAstShape =
    variables &&
    ('expression' in variables || 'excludeExpression' in variables);

  let expression: ExpressionNode | null;
  let excludeExpression: ExpressionNode | null;

  if (hasAstShape) {
    expression = expressionFromApiNode(variables.expression, index, membershipNames);
    excludeExpression = expressionFromApiNode(
      variables.excludeExpression,
      index,
      membershipNames,
    );
  } else {
    expression = expressionFromLegacyArray(variables?.include, index);
    excludeExpression = expressionFromLegacyArray(variables?.exclude, index);
  }

  // PR 2c: legacy top-level audiences/files arrays get folded into the tree
  // as membership leaves AND-joined to the variable expression. This mirrors
  // the server-side back-compat policy. Saved audiences load with everything
  // unified in one tree; the original arrays are no longer surfaced to the
  // builder UI.
  const includeMembership = membershipLeavesFromLegacyArrays(
    audiences?.include,
    files?.include,
  );
  const excludeMembership = membershipLeavesFromLegacyArrays(
    audiences?.exclude,
    files?.exclude,
  );
  expression = mergeNodesIntoTree(expression, includeMembership);
  excludeExpression = mergeNodesIntoTree(excludeExpression, excludeMembership);

  return {
    expression,
    excludeExpression,
    includeOnlyOneRecordPerHousehold:
      variables?.options?.includeOnlyOneRecordPerHousehold,
  };
};

/**
 * @deprecated Used by older callsites. Returns a flat VariableState[] by
 * flattening the new tree. Prefer `convertFiltersToBuilderFormat` which now
 * returns an expression tree directly.
 */
export const convertVariablesFiltersToBuilderFormat = (
  filters: VariableFilter[],
): VariableState[] => {
  const index = buildKeyToAttributeIndex();
  const tree = expressionFromLegacyArray(filters, index);
  return flattenLeaves(tree).map((leaf) => leaf.variable);
};

export const convertAudienceFiltersToBuilderFormat = (
  filters: MyDataFilter[]
): MyDataState[] => {
  return filters
    .map((filter) => {
      const { id, name, type, values } = filter;

      if (type === 'file') {
        return {
          id,
          name,
          type,
        };
      }

      if (type === 'audience') {
        return {
          id,
          name,
          type,
        };
      }

      return null;
    })
    .filter((filter) => filter !== null);
};

// end: filters -> builder state

export function canAddAttribute(variables, id) {
  const hasCity = variables.some(
    ({ id }) => id === 'DemoIQ___Geography___city'
  );
  const hasState = variables.some(
    ({ id }) => id === 'DemoIQ___Geography___state'
  );

  if (id === 'DemoIQ___Geography___city' && hasState) {
    return false;
  }

  if (id === 'DemoIQ___Geography___state' && hasCity) {
    return false;
  }

  return !variables.some(({ id: variable }) => variable === id);
}

export function canAddMyDataResource(resources, resourceId, resourceType) {
  return !resources.some(
    ({ id, type }) => id === resourceId && type === resourceType
  );
}

export function parseAllowedZoneTypes(zoneTypesStr) {
  if (!zoneTypesStr || zoneTypesStr.trim() === '') {
    return validZoneTypes;
  }

  const zoneTypes = zoneTypesStr.trim().split(',');

  if (!zoneTypes.every((zoneType) => validZoneTypes.includes(zoneType))) {
    return validZoneTypes;
  }

  return zoneTypes;
}

export function getIncludedIdentifiersAvailable(include) {
  return {
    emailAddOn: Boolean(
      include.find(({ attributeName }) => attributeName === 'Email Available')
    ),
    ipAddOn: Boolean(
      include.find(
        ({ attributeName }) => attributeName === 'IP Address Available'
      )
    ),
    phoneAddOn: Boolean(
      include.find(({ attributeName }) => attributeName === 'Phone Available')
    ),
  };
}

export function getEmptyFilters() {
  return {
    variables: {
      include: [],
      exclude: [],
      options: {
        includeOnlyOneRecordPerHousehold: false,
      },
    },
    files: {
      include: [],
      exclude: [],
    },
    audiences: {
      include: [],
      exclude: [],
    },
  };
}
